import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { AlertCircle, Download, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SpreadsheetViewerProps {
  url: string;
  fileName: string;
  /**
   * Optional File _id. When provided, the viewer fetches bytes through the
   * BE's /view_file/:id proxy (which forwards CORS headers), avoiding
   * browser CORS errors when the file lives in a GCS/S3 bucket that
   * doesn't allow the FE origin. Falls back to direct fetch on `url`
   * when no fileId is supplied.
   */
  fileId?: string;
}

interface SheetData {
  name: string;
  rows: string[][];
  maxCols: number;
  truncated: boolean;
  totalRows: number;
}

const MAX_ROWS_PER_SHEET = 1000;

export function SpreadsheetViewer({
  url,
  fileName,
  fileId,
}: SpreadsheetViewerProps) {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      setSheets([]);
      setActiveSheet(0);

      let phase: "fetch" | "parse" = "fetch";
      try {
        // Prefer the BE proxy (/view_file/:id) when we have a file id — it
        // forwards CORS headers, which GCS/S3 buckets typically don't.
        // Fall back to fetching `url` directly for callers that don't have
        // an id (e.g. browser blob URLs for just-uploaded files).
        // Use plain fetch — NOT axiosInstance — so the Clerk Authorization
        // header isn't attached to S3 signed URL requests.
        const fetchUrl = fileId
          ? `${import.meta.env.VITE_API_URL}/view_file/${fileId}`
          : url;
        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const buf = await res.arrayBuffer();

        phase = "parse";
        // Bound parsing up-front via `sheetRows` so huge workbooks don't
        // fully materialize into memory. The original full range is
        // preserved on `sheet["!fullref"]` for truncation detection.
        const wb = XLSX.read(buf, {
          type: "array",
          cellDates: true,
          sheetRows: MAX_ROWS_PER_SHEET,
        });
        if (cancelled) return;

        if (!wb.SheetNames.length) {
          setError("Workbook is empty");
          return;
        }

        const parsed: SheetData[] = wb.SheetNames.map((name) => {
          const sheet = wb.Sheets[name];
          const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
            header: 1,
            defval: "",
            raw: false,
            blankrows: false,
          });
          const rows: string[][] = aoa.map((row) =>
            (row as unknown[]).map((cell) =>
              cell === null || cell === undefined ? "" : String(cell)
            )
          );
          // Use the workbook's own range metadata for the total row count
          // instead of the materialized array: `!fullref` is set when
          // `sheetRows` truncated the read, otherwise `!ref` describes the
          // full sheet. This avoids any need to read the full sheet to
          // know if it was truncated.
          const fullRef =
            (sheet as unknown as { "!fullref"?: string })["!fullref"] ||
            sheet["!ref"];
          const totalRows = fullRef
            ? XLSX.utils.decode_range(fullRef).e.r + 1
            : rows.length;
          const truncated = totalRows > MAX_ROWS_PER_SHEET;
          const maxCols = rows.reduce(
            (m, r) => Math.max(m, r.length),
            0
          );
          return { name, rows, maxCols, truncated, totalRows };
        });

        setSheets(parsed);
      } catch (err) {
        // Don't log `url` — it may be a signed GCS/S3 link with credentials
        // in its query string. Log safe identifiers only.
        console.error(
          `SpreadsheetViewer ${phase} error`,
          { fileId, fileName },
          err
        );
        const detail = err instanceof Error ? err.message : String(err);
        const prefix =
          phase === "fetch"
            ? "Couldn't download this file"
            : "Couldn't parse this file";
        setError(`${prefix}: ${detail}`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [url, fileId, fileName]);

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center w-full h-full p-4">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadFile(url, fileName)}
          >
            <Download className="h-4 w-4 mr-2" /> Download File
          </Button>
        </div>
      </div>
    );
  }

  const current = sheets[activeSheet];
  if (!current) return null;

  return (
    <div className="flex flex-col w-full h-full min-h-0">
      {sheets.length > 1 && (
        <Tabs
          value={String(activeSheet)}
          onValueChange={(v) => setActiveSheet(Number(v))}
          className="px-4 pt-3"
        >
          <TabsList className="h-9">
            {sheets.map((s, i) => (
              <TabsTrigger
                key={`${s.name}-${i}`}
                value={String(i)}
                className="text-xs"
              >
                {s.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {current.truncated && (
        <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/50 border-b">
          Showing first {MAX_ROWS_PER_SHEET.toLocaleString()} of{" "}
          {current.totalRows.toLocaleString()} rows. Download the file to
          see all rows.
        </div>
      )}
      <SheetTable sheet={current} />
    </div>
  );
}

function SheetTable({ sheet }: { sheet: SheetData }) {
  const header = sheet.rows[0] ?? [];
  const dataRows = sheet.rows.length > 0 ? sheet.rows.slice(1) : [];

  if (sheet.rows.length === 0 || sheet.maxCols === 0) {
    return (
      <div className="flex items-center justify-center flex-1 p-4">
        <p className="text-sm text-muted-foreground">This sheet is empty</p>
      </div>
    );
  }

  const colCount = sheet.maxCols;

  return (
    <div className="flex-1 overflow-auto p-4">
      <table className="border-collapse text-sm">
        <thead className="sticky top-0 bg-muted z-10">
          <tr>
            {Array.from({ length: colCount }).map((_, i) => (
              <th
                key={i}
                className="border border-gray-300 px-3 py-2 text-left font-semibold whitespace-nowrap"
              >
                {header[i] || `Column ${i + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className="hover:bg-muted/50">
              {Array.from({ length: colCount }).map((_, ci) => {
                const value = row[ci] ?? "";
                return (
                  <td
                    key={ci}
                    className="border border-gray-300 px-3 py-2 whitespace-nowrap max-w-[400px] truncate"
                    title={value}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function downloadFile(url: string, fileName: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
